import React, { ReactNode, useId } from "react";
import FormLabel, { FormLabelBadgePosition } from "./FormLabel";
import InputError, { InputErrorVariant } from "./InputError";

import {
  Input as ChakraInput,
  forwardRef,
  InputGroup,
  InputProps as ChakraInputProps,
  FormLabelProps,
  Box,
  Stack,
  Flex,
} from "@chakra-ui/react";
import { MaxLengthCounter } from "./MaxLengthCounter";

export type InputProps = {
  label?: string;
  labelProps?: FormLabelProps & { notRequiredBadgeBg?: string };
  message?: ReactNode;
  required?: boolean;
  showNotRequired?: boolean;
  badgePosition?: FormLabelBadgePosition;
  error?: string;
  isError?: boolean;
  unpublished?: boolean;
  rightElement?: () => ReactNode;
  leftElement?: () => ReactNode;
  errorVariant?: InputErrorVariant;
  maxLength?: number;
} & ChakraInputProps;

const Input = forwardRef<InputProps, "input">(
  (
    {
      label = "",
      labelProps,
      message = "",
      required = false,
      showNotRequired = false,
      badgePosition = "after",
      error = "",
      isError = false,
      type = "text",
      id = "",
      placeholder = "",
      unpublished = false,
      rightElement,
      leftElement,
      maxW = undefined,
      w = undefined,
      errorVariant = "text",
      maxLength,
      ...props
    },
    ref,
  ) => {
    const randomId = useId();
    const htmlForId = id === "" ? randomId : id;
    const length = String(props.value ?? "").length;

    return (
      <Stack width="100%" gap={0}>
        {label !== "" && (
          <FormLabel
            {...labelProps}
            required={required}
            showNotRequired={showNotRequired}
            badgePosition={badgePosition}
            htmlFor={htmlForId}
            unpublished={unpublished}
          >
            {label}
          </FormLabel>
        )}
        <InputGroup h="full" maxW={maxW} w={w} mt={label !== "" ? 1 : undefined}>
          {leftElement && leftElement()}
          <ChakraInput
            placeholder={placeholder}
            _placeholder={{ opacity: 1, color: "#99A9B0" }}
            type={type}
            border="1px solid #99A9B0"
            borderRadius="2px"
            backgroundColor="#ffffff"
            fontSize="md"
            id={htmlForId}
            isInvalid={error !== "" || isError || (maxLength != null && length > maxLength)}
            _invalid={
              errorVariant === "contained"
                ? { borderColor: "caution", borderWidth: "2px" }
                : { backgroundColor: "#FAE8E8", borderColor: "caution" }
            }
            _disabled={{
              color: "#7E8C91",
              backgroundColor: "#F1F1F1",
              opacity: 1,
            }}
            {...props}
            ref={ref}
          />
          {rightElement && rightElement()}
        </InputGroup>

        <Flex
          color="gray.700"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mt={message || maxLength != null ? 1 : 0}
          fontSize="sm"
        >
          <Box as="span">{message}</Box>
          {maxLength != null && <MaxLengthCounter length={length} maxLength={maxLength} />}
        </Flex>

        {error !== "" && (
          <Box mt={errorVariant === "text" ? undefined : message == null ? 2 : "6px"}>
            <InputError variant={errorVariant}>{error}</InputError>
          </Box>
        )}
      </Stack>
    );
  },
);

export default Input;
